import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./ServiceTab.module.css";
import { API } from "../../../../request/api";
import Loading from "../../../components/Loading/Loading";
import AccessDeniedError from "../../../components/ErrorComponents/AccessDeniedError";
import { useTranslation } from "react-i18next";
import ServiceCard from "../../../components/Appointment/ServiceCard";
import {
  Button as AntDButton,
  Collapse,
  Drawer,
  Input,
  Radio,
  Space,
} from "antd";
import { ReactComponent as FilterIcon } from "../../../../assets/svg/filter.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/svg/search.svg";
import { ReactComponent as CancelIcon } from "../../../../assets/svg/cancel.svg";
import { ReactComponent as BottomArrowIcon } from "../../../../assets/svg/bottom-arrow.svg";

import Button from "../../../components/ui/Button";
import _ from "lodash";
import { Note } from "../../../../helbest";

export default function ServicesTab() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState({ value: null, label: null });
  const [value, setValue] = useState(undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [servicesData, setServicesData] = useState({
    data: [],
    charge_price: 0,
    total_verified_count: 0,
  });
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";

  const FILTERS = useMemo(
    () => [
      {
        value: "1",
        label: t("common.verified"),
      },
      {
        value: "0",
        label: t("common.unverified"),
      },
    ],
    []
  );

  const services = (filter, q) => {
    setLoading(true);
    API.get
      .getServices({ is_verified: filter, q })
      .then((response) => {
        if (response.code === 200) {
          setServicesData({
            data: response.data,
            charge_price: response.extra.total_charge_price,
            total_verified_count: response.extra.total_verified_count,
          });
        } else if (response.code === 403) {
          setError(true);
        } else {
          Note.error(response.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const debouncedSearchFn = useCallback(
    _.debounce((value) => {
      setSearchParams((prevState) => {
        prevState.set("q", value);
        return prevState;
      });
    }, 500),
    []
  );

  function clearFilter() {
    setFilter({ value: null, label: null });
    setValue(undefined);
    setFiltersOpen(false);
  }

  function submitFilter() {
    const targetStatus = FILTERS.find((filter) => filter.value === value);
    if (targetStatus) {
      setFilter({ value: targetStatus.value, label: targetStatus.label });
    }
    setFiltersOpen(false);
  }

  useEffect(() => {
    services(filter.value, search);
  }, [filter, search]);

  const items = [
    {
      styles: {
        header: {
          fontWeight: "700",
        },
      },
      label: t("common.status"),
      children: (
        <Radio.Group
          onChange={(e) => setValue(e.target.value)}
          value={value}
          defaultValue={value}
          style={{
            width: "100%",
          }}
        >
          <Space
            direction="vertical"
            style={{
              width: "100%",
              gap: "0",
            }}
          >
            {FILTERS.map((filter) => (
              <Radio
                value={filter.value}
                className={styles.radioLabel}
                style={{
                  display: "flex",
                  flexDirection: "row-reverse",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                {filter.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      ),
    },
  ];

  if (error) {
    return <AccessDeniedError />;
  }

  return (
    <>
      <div className={styles.filtersHeader}>
        <Input
          defaultValue={search}
          onChange={(e) => {
            debouncedSearchFn(e.target.value);
          }}
          variant="borderless"
          className={styles.searchInput}
          prefix={<SearchIcon className={styles.searchIcon} />}
          placeholder={t("common.search")}
        />
        <AntDButton
          className={styles.filterButton}
          type="text"
          onClick={() => {
            setFiltersOpen(true);
          }}
        >
          <FilterIcon width={16} height={16} />
        </AntDButton>
      </div>
      <div className={styles.filterBadges}>
        {filter?.value ? (
          <AntDButton
            size="small"
            className={styles.badgeButton}
            type="text"
            onClick={() => {
              setFilter({ value: null, label: null });
              setValue(null);
            }}
          >
            {filter.label}
            <CancelIcon className={styles.cancelIcon} />
          </AntDButton>
        ) : null}
      </div>
      {loading && (
        <div className={styles.Loading}>
          <Loading />
        </div>
      )}
      {servicesData.data && !loading ? (
        <div className={styles.cardsContainer}>
          {servicesData.data.map((service) => (
            <ServiceCard
              customer={service.customer}
              created_at={service.created_at}
              id={service.id}
              car={service.car}
              is_verified={service.is_verified}
            />
          ))}
        </div>
      ) : null}
      {servicesData && (
        <div className={styles.countContainer}>
          <div className={styles.Count}>
            <div>
              <span>{t("services.services_count")}</span>
              <span>{servicesData.total_verified_count}</span>
            </div>
            <div>
              <div className={styles.totalChargePriceContainer}>
                <span>{t("services.total_charge_price")}</span>
                <span className={styles.noticeDescription}>
                  {t("services.notice_message")}
                </span>
              </div>
              <div className={styles.totalChargePrice}>
                <span>OMR</span>
                <span>{servicesData.charge_price}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <Drawer
        styles={{
          header: {
            borderBottom: "none",
          },
          body: {
            paddingInline: "24px",
            paddingBlock: "0px",
          },
          footer: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            borderTop: "none",
          },
        }}
        placement="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        width={300}
        footer={
          <>
            <Button
              style_width={"49%"}
              style_backgroundColor="unset"
              style_background="linear-gradient(0deg, #FF0000, #FF0000), linear-gradient(0deg, #FF4500, #FF4500)"
              style_fontSize="type-2"
              style_fontWeight="bold"
              style_borderRadius="type-2"
              onClick={clearFilter}
            >
              {t("common.reset")}
            </Button>
            <Button
              style_width="49%"
              style_fontSize={"type-2"}
              style_fontWeight="bold"
              style_borderRadius="type-2"
              onClick={submitFilter}
            >
              {t("common.filter")}
            </Button>
          </>
        }
      >
        <Collapse
          ghost
          expandIconPosition="end"
          expandIcon={({ isActive }) => (
            <BottomArrowIcon
              transform={`rotate(${isActive ? 180 : 0})`}
              width="10px"
            />
          )}
          items={items}
        />
      </Drawer>
    </>
  );
}
